import { Box, Typography } from "@mui/material";
import { VIDEO_BG } from "../styles";

interface Props {
  isSelfSharing: boolean;
  activeRemoteShareUserId?: number;
  /** Self-share renders into a <video> (SDK requirement under WebCodecs). */
  selfVideoRef: React.RefObject<HTMLVideoElement>;
  /** Remote share renders into a <canvas>. */
  remoteCanvasRef: React.RefObject<HTMLCanvasElement>;
}

/**
 * Main-stage screen-share surface. The two canvases are kept mounted at all
 * times (display:none when inactive) so the SDK refs stay valid across
 * start/stop cycles and start*Share / start*View won't race a re-mount.
 */
export default function ScreenShareView({
  isSelfSharing,
  activeRemoteShareUserId,
  selfVideoRef,
  remoteCanvasRef,
}: Props) {
  const showSelf = isSelfSharing;
  const showRemote = !isSelfSharing && activeRemoteShareUserId !== undefined;
  const visible = showSelf || showRemote;

  return (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        bgcolor: VIDEO_BG,
        borderRadius: 1,
        overflow: "hidden",
        // Stay laid out (not display:none) so the SDK's binding to the
        // <video> element survives sharing-state transitions; only the
        // visibility flips.
        visibility: visible ? "visible" : "hidden",
        m: visible ? 1.5 : 0,
      }}
    >
      {/* Both surfaces are kept laid out at all times. We toggle
          opacity/z-index to switch which one the user sees so the SDK's
          binding to either element via startShareScreen / startShareView
          is never invalidated by a display:none flip. */}
      <video
        ref={selfVideoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
          opacity: showSelf ? 1 : 0,
          zIndex: showSelf ? 1 : 0,
          pointerEvents: showSelf ? "auto" : "none",
        }}
      />
      <canvas
        ref={remoteCanvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: showRemote ? 1 : 0,
          zIndex: showRemote ? 1 : 0,
          pointerEvents: showRemote ? "auto" : "none",
        }}
      />
      {visible && (
        <Typography
          sx={{
            position: "absolute",
            top: 8,
            left: 12,
            // Captions sit ON TOP of video — keep them readable with a
            // dark scrim regardless of theme.
            color: "common.white",
            bgcolor: "rgba(0,0,0,0.45)",
            fontSize: 12,
            px: 1,
            py: 0.25,
            borderRadius: 0.5,
          }}
        >
          {showSelf ? "You are sharing your screen" : "Screen share"}
        </Typography>
      )}
    </Box>
  );
}
